import React, { CSSProperties } from 'react'
import { Info } from 'react-feather'

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

export interface RadioOptionsProps<TValue> {
  options: {
    label: string
    value: TValue
    tooltip?: string | React.ReactNode
  }[]
  value?: TValue
  onChange?: (value: TValue) => void
  optionLabelClassName?: string
  optionLabelStyle?: CSSProperties
  inputName?: string
  isDisabled?: boolean
  variant?: 'row' | 'column'
}

export type RadioOptionsType =
  | string
  | number
  | readonly string[]
  | undefined
  | boolean

function RadioOptions<TValue extends RadioOptionsType>({
  options,
  onChange,
  value,
  optionLabelClassName,
  optionLabelStyle,
  inputName,
  isDisabled,
  variant = 'row',
}: RadioOptionsProps<TValue>) {
  function handleChange(value: TValue) {
    if (onChange) {
      onChange(value)
    }
  }

  function renderOptionLabel(
    option: RadioOptionsProps<TValue>['options'][number],
  ) {
    if (option.tooltip) {
      return (
        <div className="flex items-center">
          <div className="mr-1">{option.label}</div>
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="text-primary ml-1" size={15} />
            </TooltipTrigger>
            <TooltipContent side="bottom">{option.tooltip}</TooltipContent>
          </Tooltip>
        </div>
      )
    }

    return <div>{option.label}</div>
  }

  return (
    <>
      <div
        className={cn(
          'flex',
          variant === 'row' ? 'flex-row flex-wrap' : 'flex-col',
        )}
        style={{ gap: '10px' }}
      >
        {options.map((option) => {
          const isSelected = option.value === value
          const id = `field_${option.value}`

          return (
            <label
              key={option.label}
              className={cn(
                'flex cursor-pointer items-center rounded-sm border px-4 py-2',
                isSelected && !isDisabled
                  ? 'border-primary'
                  : 'border-gray-300',
                isDisabled && 'bg-disabled opacity-50',
                optionLabelClassName,
              )}
              style={optionLabelStyle}
            >
              <input
                id={id}
                name={inputName}
                checked={isSelected}
                type="radio"
                value={
                  typeof option.value === 'boolean'
                    ? option.value.toString()
                    : option.value
                }
                className="mr-2"
                onChange={() => handleChange(option.value)}
                disabled={isDisabled}
              />

              {renderOptionLabel(option)}
            </label>
          )
        })}
      </div>
    </>
  )
}

export default RadioOptions
